import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { isAndroidApp } from "@/utils/platform";

interface PremiumContextType {
  isPremium: boolean;
  /**
   * True only when the user actually holds a paid subscription (or native premium).
   * Ad gating must use this — during the free period `isPremium` is true for everyone,
   * which would otherwise hide ads from all visitors.
   */
  isPaidPremium: boolean;
  loading: boolean;
  /** Active subscription expiry date, if any */
  expiresAt: string | null;
  /** Force refresh premium status from DB */
  refresh: () => Promise<void>;
}

const PremiumContext = createContext<PremiumContextType>({
  isPremium: false,
  isPaidPremium: false,
  loading: true,
  expiresAt: null,
  refresh: async () => {},
});

/** Subscriptions are coming soon — everyone gets Premium free until billing launches. */
export const FREE_PREMIUM_PERIOD = true;

/**
 * Premium status is computed STRICTLY from the subscriptions table:
 *   status = 'active' AND expires_at > now()
 * Never from localStorage or in-memory flags.
 * This ensures persistence across logout/login for email + anonymous users.
 */
export const PremiumProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth();
  const [isPremium, setIsPremium] = useState(false);
  const [isPaidPremium, setIsPaidPremium] = useState(false);
  const [expiresAt, setExpiresAt] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchPremiumStatus = useCallback(async () => {
    // In-app (Play Store / PWA wrapper) users get premium unlocked automatically
    if (isAndroidApp()) {
      setIsPremium(true);
      setIsPaidPremium(true);
      setExpiresAt(null);
      setLoading(false);
      return;
    }

    // Free Premium period: subscriptions are coming soon, so every user gets
    // full Premium access until paid plans launch. Ads still run (isPaidPremium stays false).
    if (FREE_PREMIUM_PERIOD) {
      setIsPremium(true);
      setIsPaidPremium(false);
      setExpiresAt(null);
      setLoading(false);
      return;
    }

    if (!user) {
      setIsPremium(false);
      setIsPaidPremium(false);
      setExpiresAt(null);
      setLoading(false);
      return;
    }

    try {
      // Check for active subscription in DB (source of truth)
      const { data: sub } = await supabase
        .from("subscriptions")
        .select("id, status, expires_at")
        .eq("user_id", user.id)
        .in("status", ["active", "grace_period"])
        .order("expires_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      const hasActiveSub = !!(
        sub &&
        sub.expires_at &&
        new Date(sub.expires_at) > new Date()
      );

      // Also check profiles.is_premium as fallback (webhook may have set it)
      const { data: profile } = await supabase
        .from("profiles")
        .select("is_premium")
        .eq("id", user.id)
        .single();

      const premium = hasActiveSub || (profile?.is_premium ?? false);

      // NOTE: profiles.is_premium is managed server-side only (webhooks / verify-payment).
      // Clients have no UPDATE privilege on that column, so we never write it here.

      setIsPremium(premium);
      setIsPaidPremium(premium);
      setExpiresAt(sub?.expires_at ?? null);
    } catch {
      console.error("Failed to check premium status");
      setIsPremium(false);
      setIsPaidPremium(false);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchPremiumStatus();
  }, [fetchPremiumStatus]);

  return (
    <PremiumContext.Provider value={{ isPremium, isPaidPremium, loading, expiresAt, refresh: fetchPremiumStatus }}>
      {children}
    </PremiumContext.Provider>
  );
};

export const usePremiumContext = () => useContext(PremiumContext);
